import { useSignal } from '@preact/signals-react';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import appConfig from '../../../../config/app-config';
import tokenAwareFetcher from '../../../../utils/tokenAwareFetcher';
import { ErrorField } from '../../../../types/ErrorField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import { isValidEmail } from '../../../../utils/EmailUtils';
import MaskEdit from '../../../organisms/MaskEdit';
import TextEdit from '../../../molecules/TextEdit';
import NewTooltip from '../../../atoms/NewTooltip';

const COMMUNICATION_ENV = appConfig.apis.communicationApiEnv;
const CONTACTS_URL = `${COMMUNICATION_ENV.address}/contacts`;
const API_KEY = COMMUNICATION_ENV.key;
const fetcher = tokenAwareFetcher(COMMUNICATION_ENV.address, API_KEY);

interface ContactModalFormProps {
  onClose: () => void;
  onSuccess: (newContact: any) => void;
}

type Form = {
  type: string;
  name: string;
  document: string;
  phone: string;
  phoneType: 'mobile' | 'landline';
  phoneWhatsapp: boolean;
  mobile: string;
  mobileType: 'mobile' | 'landline';
  mobileWhatsapp: boolean;
  email: string;
};

export default function ContactModalForm({ onClose, onSuccess }: ContactModalFormProps) {
  const isSending = useSignal(false);
  const errorFields = useSignal<ErrorField[]>([]);
  const form = useSignal<Form>({
    type: 'PF',
    name: '',
    document: '',
    phone: '',
    phoneType: 'landline',
    phoneWhatsapp: false,
    mobile: '',
    mobileType: 'mobile',
    mobileWhatsapp: false,
    email: '',
  });

  const handleSubmit = async () => {
    errorFields.value = [];

    if (!form.value.name.trim()) {
      errorFields.value = [...errorFields.value, { fieldName: 'name', message: 'Nome é obrigatório' }];
    }

    if (!form.value.document.trim()) {
      errorFields.value = [...errorFields.value, { fieldName: 'document', message: 'CPF/CNPJ é obrigatório' }];
    }

    if (form.value.email && !isValidEmail(form.value.email)) {
      errorFields.value = [...errorFields.value, { fieldName: 'email', message: 'Email inválido' }];
    }

    if (errorFields.value.length > 0) return;

    isSending.value = true;
    const snapshot = { ...form.value };

    try {
      const response = await fetcher.post('/contacts', {
          contactType: snapshot.type === 'PF' ? 'INDIVIDUAL' : 'LEGAL_PERSON',
          fullName: snapshot.name,
          taxId: snapshot.document.replace(/[^0-9]/g, ''),
          phone: snapshot.phone.replace(/[^0-9]/g, ''),
          phoneWhatsapp: snapshot.phoneWhatsapp,
          mobile: snapshot.mobile.replace(/[^0-9]/g, ''),
          mobileWhatsapp: snapshot.mobileWhatsapp,
          email: snapshot.email,
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Contato cadastrado com sucesso');
        onSuccess({
          ...data,
          primaryEmail: data.primaryEmail || snapshot.email,
        });
        onClose();
      } else {
        const { errors, message, debugMessage } = await response.json();
        toast.error(debugMessage || message || 'Erro ao cadastrar contato');
        if (errors && errors.length > 0) {
          errorFields.value = errors.map((err: any) => ({ fieldName: err.field, message: err.message }));
        }
      }
    } catch (error) {
      toast.error('Erro ao cadastrar contato');
    } finally {
      isSending.value = false;
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Novo Contato</h3>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
          disabled={isSending.value}
        >
          {isSending.value ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto space-y-4">
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Tipo do Cadastro <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                value="PF"
                checked={form.value.type === 'PF'}
                onChange={(e) => form.value = { ...form.value, type: e.target.value }}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-900 dark:text-gray-100">Pessoa Física</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                value="PJ"
                checked={form.value.type === 'PJ'}
                onChange={(e) => form.value = { ...form.value, type: e.target.value }}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-900 dark:text-gray-100">Pessoa Jurídica</span>
            </label>
          </div>
        </div>

        <TextEdit
          label="Nome Completo"
          value={form.value.name}
          onChange={(e) => {
            form.value = { ...form.value, name: e.target.value };
            errorFields.value = errorFields.value.filter(f => f.fieldName !== 'name');
          }}
          required
          errorMessage={getErrorMessage(errorFields.value, 'name')}
        />

        <MaskEdit
          label={form.value.type === 'PF' ? 'CPF' : 'CNPJ'}
          mask={form.value.type === 'PF' ? '___.___.___-__' : '__.___.___/____-__'}
          replacement={{ _: /\d/ }}
          value={form.value.document}
          onChange={(e) => {
            form.value = { ...form.value, document: e.target.value };
            errorFields.value = errorFields.value.filter(f => f.fieldName !== 'document');
          }}
          required
          errorMessage={getErrorMessage(errorFields.value, 'document')}
        />

        <div className="flex items-end gap-2">
          <div className="flex-1">
            <MaskEdit
              key={`phone-${form.value.phoneType}`}
              label="Telefone 1"
              mask={form.value.phoneType === 'mobile' ? '(__) _____-____' : '(__) ____-____'}
              replacement={{ _: /\d/ }}
              value={form.value.phone}
              onChange={(e) => form.value = { ...form.value, phone: e.target.value }}
              prefix={
                <div className="flex bg-gray-100 dark:bg-slate-700 rounded p-0.5 mr-2">
                  <button type="button" onClick={() => form.value = { ...form.value, phoneType: 'mobile', phone: '' }} className={`px-2 py-1 text-xs rounded transition-colors ${form.value.phoneType === 'mobile' ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm font-medium' : 'text-gray-500 dark:text-gray-400'}`}>Celular</button>
                  <button type="button" onClick={() => form.value = { ...form.value, phoneType: 'landline', phone: '' }} className={`px-2 py-1 text-xs rounded transition-colors ${form.value.phoneType === 'landline' ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm font-medium' : 'text-gray-500 dark:text-gray-400'}`}>Fixo</button>
                </div>
              }
            />
          </div>
          <div className="pb-2">
            <NewTooltip content="É WhatsApp" alignment="top">
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.value.phoneWhatsapp}
                  onChange={(e) => form.value = { ...form.value, phoneWhatsapp: e.target.checked }}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <FaWhatsapp className="text-green-500" />
              </label>
            </NewTooltip>
          </div>
        </div>

        <div className="flex items-end gap-2">
          <div className="flex-1">
            <MaskEdit
              key={`mobile-${form.value.mobileType}`}
              label="Telefone 2"
              mask={form.value.mobileType === 'mobile' ? '(__) _____-____' : '(__) ____-____'}
              replacement={{ _: /\d/ }}
              value={form.value.mobile}
              onChange={(e) => form.value = { ...form.value, mobile: e.target.value }}
              prefix={
                <div className="flex bg-gray-100 dark:bg-slate-700 rounded p-0.5 mr-2">
                  <button type="button" onClick={() => form.value = { ...form.value, mobileType: 'mobile', mobile: '' }} className={`px-2 py-1 text-xs rounded transition-colors ${form.value.mobileType === 'mobile' ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm font-medium' : 'text-gray-500 dark:text-gray-400'}`}>Celular</button>
                  <button type="button" onClick={() => form.value = { ...form.value, mobileType: 'landline', mobile: '' }} className={`px-2 py-1 text-xs rounded transition-colors ${form.value.mobileType === 'landline' ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-white shadow-sm font-medium' : 'text-gray-500 dark:text-gray-400'}`}>Fixo</button>
                </div>
              }
            />
          </div>
          <div className="pb-2">
            <NewTooltip content="É WhatsApp" alignment="top">
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.value.mobileWhatsapp}
                  onChange={(e) => form.value = { ...form.value, mobileWhatsapp: e.target.checked }}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <FaWhatsapp className="text-green-500" />
              </label>
            </NewTooltip>
          </div>
        </div>

        <TextEdit
          label="Email"
          value={form.value.email}
          onChange={(e) => {
            form.value = { ...form.value, email: e.target.value };
            errorFields.value = errorFields.value.filter(f => f.fieldName !== 'email');
          }}
          errorMessage={getErrorMessage(errorFields.value, 'email')}
        />
      </div>
    </div>
  );
}
